import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Activity, CheckCircle, BarChart3, Heart, Clock } from 'lucide-react';
import Seo from '../components/Seo';

const HomePage = () => {
  const features = [
    {
      icon: <Heart className="w-12 h-12 text-cyan-300" />,
      title: 'Compassion',
      description: 'Intelligent coordination reduces stress and supports retention.',
    },
    {
      icon: <CheckCircle className="w-12 h-12 text-cyan-300" />,
      title: 'Quality',
      description: 'Align resources to demand, ensuring timely, high-quality care.',
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-cyan-300" />,
      title: 'Efficiency',
      description: 'Lower overtime and delays while improving patient throughput.',
    },
  ];

  const problems = [
    {
      stat: '15-20%',
      title: 'Beds Blocked',
      description: 'Patients ready for discharge remain hospitalized due to coordination gaps, reducing available capacity for incoming admissions.',
    },
    {
      stat: '30%+',
      title: 'Burnout & Turnover Rate',
      description: 'Communication delays slow patient flow, while admin burden and stress drive clinicians to leave at alarming rates.',
    },
    {
      stat: '$2M+/mon',
      title: 'Revenue Lost',
      description: 'Every idle bed or delayed admission costs hospitals significant revenue and worsens ED crowding.',
    },
  ];

  const solutions = [
    {
      title: 'Real-Time Coordination',
      description: 'Break silos between care teams with AI agents that keeps patients, staff, and information moving seamlessly',
      image: 'https://images.pexels.com/photos/7108145/pexels-photo-7108145.jpeg',
    },
    {
      title: 'Agile Healthcare',
      description: 'Adapt in minutes, not days. Align people and processes across departments to prevent backups and improve throughput.',
      image: 'https://images.pexels.com/photos/19213884/pexels-photo-19213884.jpeg',
    },
  ];

  const benefits = [
    {
      icon: <CheckCircle className="w-8 h-8 text-cyan-300" />,
      title: 'Quality of Care',
      description: 'Fewer delays and smoother transitions translate to safer care. DeltaOps improves timeliness, communication, and patient satisfaction through proactive coordination.',
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-cyan-300" />,
      title: 'Cost & Revenue Impact',
      description: 'By unlocking under-used capacity and preventing bottlenecks, DeltaOps cuts overtime and travel costs while recovering lost revenue from delayed discharges.',
    },
    {
      icon: <Users className="w-8 h-8 text-cyan-300" />,
      title: 'Workforce Well-Being',
      description: 'AI-agentic scheduling and real-time insights reduce burnout and improve work-life balance, building a more resilient and satisfied care team.',
    },
  ];

  const impactMetrics = [
    { value: '25%', label: 'Productivity Boost', description: 'Through dynamic nurse allocation' },
    { value: '3x', label: 'ROI', description: 'Reduces reliance on costly travel nurses' },
    { value: '0.5', label: 'Days Reduced', description: 'Average length of stay reduction' },
  ];

  return (
    <div className="homepage" data-testid="home-page">
      <Seo
        title="DeltaOps AI | Healthcare Flow Optimization"
        description="AI-powered coordination that unlocks patient flow, staffing efficiency, and care transitions across hospitals and home health."
        image="https://images.pexels.com/photos/3844581/pexels-photo-3844581.jpeg"
        imageAlt="Hospital care team coordinating patient flow"
      />
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
        style={{
          background: `linear-gradient(120deg, rgba(2, 12, 27, 0.95), rgba(4, 120, 149, 0.8)), url('https://images.pexels.com/photos/3844581/pexels-photo-3844581.jpeg') center/cover`,
        }}
        data-testid="hero-section"
      >
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center fade-in-up">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
              AI Powered Healthcare Flow Optimization
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-cyan-100">
              Unlock Hidden Capacity in Your Existing Systems
            </p>
            <p className="text-lg md:text-xl mb-8 text-cyan-200">
              Faster Patient Flow. Smarter Staffing. Seamless Communication.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contact"
                className="btn btn-primary text-lg px-8 py-4"
                data-testid="hero-cta-button"
              >
                Request a Demo <ArrowRight className="ml-2" />
              </Link>
              <Link
                to="/success-stories"
                className="btn btn-secondary text-lg px-8 py-4"
                data-testid="hero-secondary-cta"
              >
                View Success Stories
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Scrolling Features */}
      <section className="py-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-900" data-testid="features-section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="text-center p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur hover:border-cyan-300/60 transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="flex justify-center mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-bold mb-3 text-white">{feature.title}</h3>
                <p className="text-slate-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Are We */}
      <section className="py-20 bg-[#030d1f]" data-testid="who-are-we-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-cyan-300 font-semibold uppercase tracking-wide mb-2">Who Are We</p>
            <h2 className="text-4xl font-bold mb-4 text-white">
              AI that understands the front line
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              A healthcare operations intelligence layer
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                DeltaOps uses <span className="font-semibold text-cyan-300">agentic AI</span> to continuously learn from real-world signals such as messages, shift notes, and team communications, that traditional EHRs overlook.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                By combining these hidden signals with advanced optimization, DeltaOps dynamically aligns staffing, patient flow, and care coordination across units and campuses.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-cyan-900/50 ring-1 ring-white/10">
              <img
                src="https://images.pexels.com/photos/17486100/pexels-photo-17486100.png"
                alt="AI Healthcare Technology"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-900 text-white" data-testid="impact-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-cyan-200 font-semibold uppercase tracking-wide mb-2">Success Story</p>
            <h2 className="text-4xl font-bold mb-4">Our Impact in Action</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {impactMetrics.map((metric, index) => (
              <div
                key={index}
                className="text-center p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/10"
                data-testid={`impact-metric-${index}`}
              >
                <div className="text-6xl font-bold mb-2">{metric.value}</div>
                <h3 className="text-xl font-semibold mb-2">{metric.label}</h3>
                <p className="text-cyan-100">{metric.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <h3 className="text-3xl font-bold mb-6">Better Culture and Better Care</h3>
            <p className="text-xl text-cyan-100 max-w-3xl mx-auto mb-8">
              Improves how teams coordinate, not just how they staff — creating smoother patient flow, fewer handoff delays, and a more connected care culture across units.
            </p>
            <Link
              to="/success-stories"
              className="inline-flex items-center btn bg-white/10 text-white border border-white/30 backdrop-blur hover:bg-white/20"
              data-testid="impact-cta-button"
            >
              View Our Success Stories <ArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* The Current Problem */}
      <section className="py-20 bg-[#020a17]" data-testid="problems-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-rose-300 font-semibold uppercase tracking-wide mb-2">The Current Problem</p>
            <h2 className="text-4xl font-bold mb-4 text-white">
              When Patient Flow Breaks, Everything Suffers
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {problems.map((problem, index) => (
              <div
                key={index}
                className="p-8 border-2 border-rose-500/30 rounded-xl bg-white/5 backdrop-blur hover:border-rose-300 transition-all"
                data-testid={`problem-card-${index}`}
              >
                <div className="text-5xl font-bold text-rose-300 mb-3">{problem.stat}</div>
                <h3 className="text-xl font-bold mb-3 text-white">{problem.title}</h3>
                <p className="text-slate-300">{problem.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 bg-slate-900" data-testid="solutions-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-cyan-300 font-semibold uppercase tracking-wide mb-2">Solutions</p>
            <h2 className="text-4xl font-bold mb-4 text-white">DeltaOps AI is here to fix this</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              More than just a scheduling tool — an AI coordination platform for patient flow and workforce resilience.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="bg-white/5 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-white/10 backdrop-blur"
                data-testid={`solution-card-${index}`}
              >
                <img
                  src={solution.image}
                  alt={solution.title}
                  className="w-full h-64 object-cover"
                />
                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-3 text-white">{solution.title}</h3>
                  <p className="text-slate-300">{solution.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-[#030c1f]" data-testid="benefits-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-cyan-300 font-semibold uppercase tracking-wide mb-2">Benefits</p>
            <h2 className="text-4xl font-bold mb-4 text-white">Why Healthcare Industry Choose DeltaOps AI</h2>
            <p className="text-xl text-slate-300">
              Unlock hidden capacity and keep care moving with intelligent coordination.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="p-8 bg-gradient-to-br from-white/5 via-cyan-900/10 to-transparent rounded-xl border border-white/10"
                data-testid={`benefit-card-${index}`}
              >
                <div className="mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold mb-3 text-white">{benefit.title}</h3>
                <p className="text-slate-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-cyan-600/20 via-slate-900 to-slate-950 text-white" data-testid="final-cta-section">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Healthcare Operations?</h2>
          <p className="text-xl mb-8 text-cyan-100 max-w-2xl mx-auto">
            Join leading healthcare providers using DeltaOps AI to improve patient flow, reduce costs, and support their teams.
          </p>
          <Link
            to="/contact"
            className="btn bg-white/10 text-white border border-white/30 hover:bg-white/20 text-lg px-8 py-4"
            data-testid="final-cta-button"
          >
            Request a Free Demo <ArrowRight className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
